import { trpc } from '@/main'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { deriveProfiles } from '@/hooks/use-root-search'
import { noop } from '@tanstack/react-query'

/**
 * Layout for a selected team (`/teams/$teamId/*`). It renders the animated tab Outlet (real content),
 * so per AGENTS.md it OWNS the teamId-keyed chrome (getTeam + members list) that all five tab children
 * read — declared on its `context` and prefetched from there, so the loader and every reader below name
 * the one object. The chrome ABOVE this match (teams.tsx's report pane, the members panel) can't be
 * handed that context, so it shares the factories instead.
 *
 * `prefetchReport` is NOT lifted here: only report/board/distribution read the report, while
 * evaluate/evaluation do not, so it stays in those three leaves (lifting it would fire an unused report
 * fetch on the evaluate/evaluation tabs).
 *
 * The `AnimatedOutlet` now lives in the parent `teams.tsx` (wrapping the `<Outlet/>` that renders this
 * layout) so the enter animation plays once when the report panel first appears and never re-animates on
 * tab/team switches; this layout just renders a plain `<Outlet/>` for its tab children.
 */
export const Route = createFileRoute('/_app/teams/$teamId')({
  // EXACTLY the search the two keys below are built from — nothing else may re-run this loader.
  loaderDeps: ({ search }) => ({
    first: search.first,
    second: search.second,
    type: search.type,
    o: search.o,
    od: search.od,
    q: search.q,
  }),
  context: ({ deps, params }) => {
    // The team subviews are never the /report page, so isReportPage is false here; `deriveProfiles` is
    // what `useRootSearch` already applies at render, so both sides see the same `first`/`second`.
    const search = { ...deps, ...deriveProfiles(deps, params, false) }
    const teamId = Number(params.teamId)
    // The filters are whichever SELECTED profile is this team — the tab children and the chrome above
    // this match all read the field below, so this derivation exists once, here.
    const teamProfile =
      search.first?.type === 'team' && search.first.id === teamId
        ? search.first
        : search.second?.type === 'team' && search.second.id === teamId
          ? search.second
          : undefined
    return {
      teamQueryOptions: trpc.teams.getTeam.queryOptions({
        teamId,
        filters: {
          type: search.type,
          evaluation: teamProfile?.evaluation,
          members: teamProfile && 'members' in teamProfile ? teamProfile.members : undefined,
          sort: search.o,
          sortDirection: search.od,
        },
      }),
      // The members list as a visit lands on it. The panel that renders it sits in the parent
      // `teams.tsx`, above this match, so it calls the same factory with its live `q`.
      teamMembersInfiniteQueryOptions: trpc.teams.searchMembersInfinite.infiniteQueryOptions(
        { teamId: Number(params.teamId), q: deps.q, o: deps.o, od: deps.od, type: deps.type },
        { getNextPageParam: (lastPage) => lastPage.nextCursor },
      ),
    }
  },
  loader: ({ context }) => {
    context.queryClient.query(context.teamQueryOptions).catch(noop)
    context.queryClient.infiniteQuery(context.teamMembersInfiniteQueryOptions).catch(noop)
  },
  component: RouteComponent,
  // No pendingComponent: this passthrough layout just renders <AnimatedOutlet/>. The data-cold case is
  // covered by the parent teams.tsx (<Suspense fallback={ReportPanelSkeleton}>), and each tab leaf
  // (report/board/distribution) owns its own tab-specific skeleton — a generic one here would mismatch.
})

function RouteComponent() {
  return <Outlet />
}
